import { useMemo } from 'react';
import { VisionCanvas } from '@/components/common/vision-canvas.tsx';
import { CollaborationInitializer } from '@/initializers/collaboration-initializer';
import type { BaseAsset } from '@akavirtual/deai-web-core';

// The JSON you provided, now used as the default scene template.
const DEFAULT_SCENE_TEMPLATE: BaseAsset = {
    "uuid": "1f2a3b4c-5d6e-4f7a-8b9c-0d1e2f3a4b5c",
    "name": "Model Preview Scene",
    "type": "application/scene+json",
    "status": "active",
    "latestVersionId": 52,
    "createdAt": "2025-10-07T07:57:43.000Z",
    "updatedAt": null,
    "version": 1,
    "data": {
        "environment": {
            "uuid": "7e8a36c1-2a6c-4b6a-9383-3e0e71f76827",
            "name": "mainSceneEnv",
            "type": "model/gltf+json",
            "status": "active",
            "latestVersionId": 1,
            "createdAt": "2025-10-07T07:57:43.000Z",
            "updatedAt": null,
            "versions": [
                {
                    "id": 1,
                    "assetUuid": "7e8a36c1-2a6c-4b6a-9383-3e0e71f76827",
                    "version": 1,
                    "data": {},
                    "status": "active",
                    "createdAt": "2025-10-07T07:57:43.000Z",
                    "updatedAt": null
                }
            ],
            "version": 1,
            "data": {}
        },
        "entities": [
            {
                "name": "MainCamera",
                "transform": {
                    "position": [
                        0,
                        1.3,
                        -1.5
                    ],
                    "rotation": [
                        0,
                        180,
                        0
                    ],
                    "scale": [
                        1,
                        1,
                        1
                    ]
                },
                "components": [
                    {
                        "type": "Camera",
                        "properties": {
                            "fieldOfView": 60,
                            "nearClip": 0.1,
                            "farClip": 1000
                        }
                    }
                ]
            },
            {
                "name": "TestAvatar1",
                "transform": {
                    "position": [
                        0,
                        0,
                        0
                    ],
                    "rotation": [
                        0,
                        0,
                        0
                    ],
                    "scale": [
                        1,
                        1,
                        1
                    ]
                },
                "components": [
                    {
                        "type": "Model",
                        "properties": {
                            "asset": {
                                "uuid": "067c7bc2-7703-4f10-a7b8-fa7b3d2cdba6",
                            }
                        }
                    },
                    {
                        "type": "Animator",
                        "properties": {
                            "controller": {
                                "uuid": "9b0c1d2e-3f4a-4c9d-a4e3-f2a1b0c9d8e7",
                            }
                        }
                    },
                    {
                        "type": "Avatar",
                        "properties": {
                            "abilitiesConfig": [
                                {
                                    "type": "speak",
                                    "properties": {
                                        "phonemeMultipliers": {
                                            "aa": 1.2,
                                            "ou": 0.8
                                        }
                                    }
                                }
                            ]
                        }
                    }
                ]
            },
            {
                "name": "GameManager",
                "transform": {
                    "position": [
                        0,
                        0,
                        0
                    ],
                    "rotation": [
                        0,
                        0,
                        0
                    ],
                    "scale": [
                        1,
                        1,
                        1
                    ]
                },
                "components": [
                    {
                        "type": "GameRules",
                        "properties": {
                            "settings": {
                                "uuid": "7f8a9b0c-1d2e-4a9b-e6c5-d4e3f2a1b0c9",
                                "name": "gameSettings",
                                "type": "application/game-settings+json",
                                "status": "active",
                                "latestVersionId": 48,
                                "createdAt": "2025-10-07T07:57:43.000Z",
                                "updatedAt": null,
                                "versions": [
                                    {
                                        "id": 48,
                                        "assetUuid": "7f8a9b0c-1d2e-4a9b-e6c5-d4e3f2a1b0c9",
                                        "version": 1,
                                        "data": {
                                            "difficulty": "normal",
                                            "scoreMultiplier": 1.5,
                                            "enableHints": true
                                        },
                                        "status": "active",
                                        "createdAt": "2025-10-07T07:57:43.000Z",
                                        "updatedAt": null
                                    }
                                ],
                                "version": 1,
                                "data": {
                                    "difficulty": "normal",
                                    "scoreMultiplier": 1.5,
                                    "enableHints": true
                                }
                            }
                        }
                    }
                ]
            }
        ]
    }
} as BaseAsset;


type AnimatorPreviewProps = {
  /** The UUID of the model asset to be injected into the scene. */
  assetId: string;
  /** An optional, custom scene definition. If not provided, a default template is used. */
  sceneTemplate?: BaseAsset;
};

export const AnimatorPreview = ({
  assetId,
  sceneTemplate = DEFAULT_SCENE_TEMPLATE,
}: AnimatorPreviewProps) => {
  const definition = useMemo(() => {
    if (!sceneTemplate || !assetId) return null;

    const newDefinition = JSON.parse(JSON.stringify(sceneTemplate));

    const animatorEntity = newDefinition.data?.entities?.find((entity: any) =>
      entity.components.some((component: any) => component.type === 'Animator')
    );

    if (animatorEntity) {
      const animatorComponent = animatorEntity.components.find(
        (component: any) => component.type === 'Animator'
      );
      if (animatorComponent) {
        animatorComponent.properties.controller.uuid = assetId;
      }
    }

    return newDefinition as BaseAsset;
  }, [sceneTemplate, assetId]); 

  if (!definition) {
    return (
      <div className="flex h-full w-full items-center justify-center text-destructive">
        Invalid Scene Template or Asset ID provided.
      </div>
    );
  }

  return (
    <VisionCanvas
      key={definition.uuid}
      instanceId={`agent-scene-${definition.uuid}`}
      className='h-full w-full'
      definition={definition}
      initializer={CollaborationInitializer}
      assetBaseUrl={window.location.origin}
    />
  );
};